import { ChangeDetectorRef, Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Meeting, MeetingAccessLevel, MeetingShare, MeetingsService, filterOfferedAccessLevels } from '../../meetings.service'
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogTitle } from '@angular/material/dialog'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatCheckbox } from '@angular/material/checkbox'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatDivider } from '@angular/material/divider'
import { FormsModule } from '@angular/forms'
import { MatAutocompleteModule, MatOption } from '@angular/material/autocomplete'
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { filter, firstValueFrom, map } from 'rxjs'
import { ToastService } from '../../toast.service'
import { User, UsersService } from '../../users.service'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { constructNameForUser } from '../../utils'
import { SpinnerComponent } from '../../spinner/spinner.component'
import { UserNamePipe } from '../../user-name.pipe'
import { AsyncPipe } from '@angular/common'
import { AvatarComponent } from '../../avatar/avatar.component'

@Component({
  selector: 'app-share-meeting-dialog',
  standalone: true,
  imports: [
    MatButton,
    MatIconButton,
    MatCheckbox,
    TranslatePipe,
    TranslateDirective,
    MatDialogClose,
    MatDialogTitle,
    MatDivider,
    FormsModule,
    MatOption,
    MatAutocompleteModule,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger,
    MatIcon,
    MatTooltip,
    SpinnerComponent,
    AvatarComponent,
    UserNamePipe,
    AsyncPipe,
  ],
  templateUrl: './share-meeting-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './share-meeting-dialog.component.scss',
})
export class ShareMeetingDialogComponent implements OnInit {
  meetingsService = inject(MeetingsService)
  toastService = inject(ToastService)
  usersService = inject(UsersService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  changeDetectorRef = inject(ChangeDetectorRef)

  updateShareLevelMutation = this.meetingsService.updateMeetingAccessLevel(this.meeting.id)
  shareToUserMutation = this.meetingsService.shareToUser(this.meeting.id)
  removeShareMutation = this.meetingsService.removeShareMutation(this.meeting.id)

  isSharePending$ = this.shareToUserMutation.result$.pipe(map((result) => result.isPending))

  readonly levels: MeetingAccessLevel[] = filterOfferedAccessLevels(['COMMENTER', 'REVIEWER', 'EDITOR', 'RESTRICTED'], this.meeting.allowRestrictedUsers)

  canEdit = false
  currentUserId = this.usersService.currentUser$.value?.id ?? null
  userToShareTo: User | null = null
  email = ''
  level: MeetingAccessLevel = 'COMMENTER'
  notifyByEmail = true
  filteredTeamUsers: User[] = []
  teamUsers: User[] = []

  constructor(@Inject(MAT_DIALOG_DATA) public meeting: Meeting) {}

  ngOnInit() {
    this.initializeTeamUsers()
    this.canEdit = this.meeting.accessLevel === 'FULL_ACCESS'
  }

  async updateShareLevel(share: MeetingShare, level: MeetingAccessLevel) {
    try {
      await this.updateShareLevelMutation.mutateAsync({ sharedUserId: share.sharedUserId, level })
      share.level = level
      this.changeDetectorRef.detectChanges()
      this.toastService.show('MEETINGS.SHARE_DIALOG.UPDATED_NOTIFICATION')
    } catch (error) {
      this.toastService.show('MEETINGS.SHARE_DIALOG.UPDATE_ERROR')
    }
  }

  filterTeamUsers(input: string) {
    this.filteredTeamUsers = this.teamUsers
      .filter((user) => !this.meeting.users.map((meetingOwner) => meetingOwner.id).includes(user.id))
      .filter((user) => !this.meeting.shares.map((share) => share.sharedUserId).includes(user.id))
      .filter((user) => user.id !== this.usersService.currentUser$.value?.id)
      .filter((user) => (constructNameForUser(user) + user.email).toLowerCase().includes(input))
  }

  displayUserOnAutocomplete(user: User): string {
    return user !== null && typeof user === 'object' ? constructNameForUser(user) : ''
  }

  async shareMeeting(): Promise<void> {
    const userToShareTo = this.resolveShareTarget()
    if (userToShareTo === null) {
      return
    }
    try {
      const share = await this.shareToUserMutation.mutateAsync({
        sharedUserId: userToShareTo.id,
        level: this.level,
        notifyByEmail: this.notifyByEmail,
      })
      this.meeting.shares.push(share)
      this.resetShareForm()
      this.toastService.show('MEETINGS.SHARE_DIALOG.SHARED_NOTIFICATION')
    } catch (error) {
      this.toastService.show('MEETINGS.SHARE_DIALOG.SHARE_ERROR')
    }
  }

  async removeShare(sharedUser: User) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.SHARE_DIALOG.REMOVE_SHARE.TITLE'),
        description: this.translateService.instant('MEETINGS.SHARE_DIALOG.REMOVE_SHARE.DESCRIPTION', { userName: constructNameForUser(sharedUser) }),
      },
    })

    const result = await firstValueFrom(dialogRef.afterClosed())
    if (result) {
      await this.removeShareMutation.mutateAsync(sharedUser.id)
      this.meeting.shares = this.meeting.shares.filter((share) => share.sharedUserId !== sharedUser.id)
      this.changeDetectorRef.detectChanges()
      this.toastService.show('MEETINGS.SHARE_DIALOG.SHARE_REMOVED_NOTIFICATION')
    }
  }

  isValidInput() {
    const userToShareTo = this.resolveShareTarget()
    if (userToShareTo === null) {
      return false
    }

    const isAlreadyShared = this.meeting.shares.some((share) => share.sharedUserId === userToShareTo.id)
    const isMeetingOwner = this.meeting.users.some((user) => user.id === userToShareTo.id)
    return !isAlreadyShared && !isMeetingOwner && userToShareTo.id !== this.currentUserId
  }

  private resolveShareTarget(): User | null {
    return this.userToShareTo ?? this.findTeamUserByEmail()
  }

  private findTeamUserByEmail(): User | null {
    const email = this.email.trim().toLowerCase()
    return this.teamUsers.find((user) => user.email.toLowerCase() === email) ?? null
  }

  private resetShareForm() {
    this.userToShareTo = null
    this.email = ''
    this.level = 'COMMENTER'
  }

  private initializeTeamUsers() {
    this.usersService
      .getTeamUsers()
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((users) => (this.teamUsers = users!))
  }
}
